import type { VhodyashcheeUvedomlenie } from '../obrashcheniya/api';

/**
 * ВХОДЯЩЕЕ УВЕДОМЛЕНИЕ — ЧИСТЫЕ ПРАВИЛА СТОПКИ (`s720`, приёмка 06.10.2026).
 *
 * Слова задания: «группировка по диалогу, максимум 3 карточки … скрытие через 10 секунд, при
 * наведении пауза, раскрытый ответ остаётся. Закрытие не означает прочтение. Не дублировать
 * активный видимый диалог».
 *
 * Здесь только счёт: какие карточки стоят на экране и что с ними делает новое сообщение, время и
 * нажатие. Таймеры, звук и вкладки — у компонента; сервер этими правилами не трогается вовсе
 * (закрыть карточку — не «прочитано», раскрыть ответ — не блокировка).
 */
export const MAKS_KARTOCHEK = 3;
export const SKRYT_CHEREZ_MS = 10_000;

export interface Kartochka {
  /** Ключ группы — номер обращения: одна карточка на диалог. */
  obrashchenieId: number;
  /** Последнее сообщение диалога — его текст и стоит на карточке. */
  poslednee: VhodyashcheeUvedomlenie;
  /** Сколько сообщений диалога пришло, пока карточка на экране. */
  soobshcheniy: number;
  /** Номера сообщений группы — чтобы повтор ленты не посчитался вторым сообщением. */
  ids: number[];
  /** Когда карточку пора скрыть (время экрана, мс); `null` — не скрывается (раскрыт ответ). */
  skrytV: number | null;
  /** Быстрый ответ раскрыт: карточка остаётся, пока человек сам её не свернёт или не закроет. */
  otvet: boolean;
}

export interface Stopka {
  /** Карточки на экране, старые сверху, новая — внизу у края. Не больше трёх. */
  kartochki: Kartochka[];
  /** Диалоги с новыми сообщениями, которым не хватило места в трёх карточках. */
  zaPredelom: number[];
}

export const PUSTAYA: Stopka = { kartochki: [], zaPredelom: [] };

/**
 * Пришли сообщения. `vidimye` — диалоги, открытые и видимые на этом экране прямо сейчас: по ним
 * карточки нет — человек и так смотрит в эту переписку.
 */
export function prinyat(
  stopka: Stopka,
  novye: VhodyashcheeUvedomlenie[],
  seychas: number,
  vidimye: ReadonlySet<number> = new Set(),
): Stopka {
  let kartochki = stopka.kartochki;
  let zaPredelom = stopka.zaPredelom;

  for (const s of novye) {
    if (vidimye.has(s.obrashchenie_id)) continue;

    const est = kartochki.find((k) => k.obrashchenieId === s.obrashchenie_id);

    if (est !== undefined) {
      if (est.ids.includes(s.id)) continue;
      // Тот же диалог — та же карточка: текст последнего, счётчик больше, отсчёт заново.
      kartochki = kartochki.map((k) => k !== est ? k : {
        ...k,
        poslednee: s,
        soobshcheniy: k.soobshcheniy + 1,
        ids: [...k.ids, s.id],
        skrytV: k.otvet ? null : seychas + SKRYT_CHEREZ_MS,
      });
      continue;
    }

    const novaya: Kartochka = {
      obrashchenieId: s.obrashchenie_id, poslednee: s, soobshcheniy: 1, ids: [s.id],
      skrytV: seychas + SKRYT_CHEREZ_MS, otvet: false,
    };

    if (kartochki.length < MAKS_KARTOCHEK) {
      kartochki = [...kartochki, novaya];
      zaPredelom = zaPredelom.filter((id) => id !== s.obrashchenie_id);
      continue;
    }

    // Мест нет. Карточку с раскрытым ответом не вытесняем: в ней набирают текст.
    const zhertva = kartochki.find((k) => !k.otvet);
    if (zhertva === undefined) {
      if (!zaPredelom.includes(s.obrashchenie_id)) zaPredelom = [...zaPredelom, s.obrashchenie_id];
      continue;
    }

    // Вытесняется самая старая: её диалог уходит в счётчик «ещё диалогов», новая встаёт у края.
    kartochki = [...kartochki.filter((k) => k !== zhertva), novaya];
    zaPredelom = [...zaPredelom.filter((id) => id !== s.obrashchenie_id && id !== zhertva.obrashchenieId), zhertva.obrashchenieId];
  }

  return kartochki === stopka.kartochki && zaPredelom === stopka.zaPredelom ? stopka : { kartochki, zaPredelom };
}

/** Время идёт: карточки, чей срок вышел, скрываются. `naPauze` — над какими стоит курсор (не скрываются). */
export function poVremeni(stopka: Stopka, seychas: number, naPauze: ReadonlySet<number> = new Set()): Stopka {
  const kartochki = stopka.kartochki.filter(
    (k) => k.otvet || k.skrytV === null || naPauze.has(k.obrashchenieId) || k.skrytV > seychas,
  );
  if (kartochki.length === stopka.kartochki.length) return stopka;

  // Последняя карточка ушла сама — счётчик «ещё диалогов» без неё не висит: он её спутник.
  return { kartochki, zaPredelom: kartochki.length === 0 ? [] : stopka.zaPredelom };
}

/** Курсор ушёл с карточки — её отсчёт начинается заново (пока читали, время не шло). */
export function prodlit(stopka: Stopka, obrashchenieId: number, seychas: number): Stopka {
  return {
    ...stopka,
    kartochki: stopka.kartochki.map((k) =>
      k.obrashchenieId === obrashchenieId && !k.otvet ? { ...k, skrytV: seychas + SKRYT_CHEREZ_MS } : k),
  };
}

/** Закрыть карточку рукой. Это не «прочитано»: серверу об этом ничего не уходит. */
export function zakryt(stopka: Stopka, obrashchenieId: number): Stopka {
  const kartochki = stopka.kartochki.filter((k) => k.obrashchenieId !== obrashchenieId);

  return { kartochki, zaPredelom: kartochki.length === 0 ? [] : stopka.zaPredelom };
}

/** Раскрыть или свернуть быстрый ответ. Раскрытая карточка не скрывается сама. */
export function otvet(stopka: Stopka, obrashchenieId: number, raskryt: boolean, seychas: number): Stopka {
  return {
    ...stopka,
    kartochki: stopka.kartochki.map((k) => k.obrashchenieId !== obrashchenieId ? k : {
      ...k, otvet: raskryt, skrytV: raskryt ? null : seychas + SKRYT_CHEREZ_MS,
    }),
  };
}

/** Диалог стал видимым на экране (его открыли) — карточка по нему больше не нужна. */
export function ubratVidimye(stopka: Stopka, vidimye: ReadonlySet<number>): Stopka {
  if (!stopka.kartochki.some((k) => !k.otvet && vidimye.has(k.obrashchenieId)) && !stopka.zaPredelom.some((id) => vidimye.has(id))) {
    return stopka;
  }
  const kartochki = stopka.kartochki.filter((k) => k.otvet || !vidimye.has(k.obrashchenieId));

  return { kartochki, zaPredelom: kartochki.length === 0 ? [] : stopka.zaPredelom.filter((id) => !vidimye.has(id)) };
}
